import { AnalystProto, ScenarioAnalysis } from "build-ts/analyst";
import { Header } from "build-ts/metadata";
import { SlackChannel, SlackUser } from "build-ts/slack_notification";
import showToast from "hooks/useCustomToast";

/**
 * Get Analyst
 *
 * @param {string} analystId - id of the analyst to get.
 * @returns {Promise<AnalystProto>} A promise that resolves to the analyst.
 * @throws {Error} Throws an error if the request fails.
 */
export const getAnalyst = async (analystId: string) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_ANALYST!}/${analystId}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Error getting analyst", "", "error");
			return null;
		}
		const data = await response.json();
		return AnalystProto.fromJSON(data);
	} catch (error) {
		showToast("Error getting analyst", "", "error");
		return null;
	}
};

/**
 * Create Analyst
 *
 * @param {AnalystProto} analyst - analyst proto to create a new analyst.
 * @returns {Promise<AnalystProto>} A promise that resolves to the created analyst.
 * @throws {Error} Throws an error if the request fails.
 */
export const createAnalyst = async (analyst: AnalystProto) => {
	try {
		const response = await fetch(process.env.REACT_APP_CREATE_ANALYST!, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(AnalystProto.toJSON(analyst)),
			credentials: "include",
		});

		if (!response.ok) {
			const error = await response.json();
			showToast("Error creating analyst", error.errorMsg, "error");
			return null;
		}

		return AnalystProto.fromJSON(await response.json());
	} catch (error) {
		showToast("Error creating analyst", "", "error");
		return null;
	}
};

/**
 * Update Analyst
 *
 * @param {AnalystProto} analyst - analyst proto to update the analyst.
 * @returns {Promise<AnalystProto>} A promise that resolves to the updated analyst.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateAnalyst = async (analyst: AnalystProto, updateDataScope: boolean = false) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_UPDATE_ANALYST!}?updateDataScope=${updateDataScope}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(AnalystProto.toJSON(analyst)),
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Error creating analyst", "", "error");
			return null;
		}

		return AnalystProto.fromJSON(await response.json());
	} catch (error) {
		showToast("Error creating analyst", "", "error");
		return null;
	}
};

/**
 * Put scenarios into an analyst. Backend merges the given scenarios (by id) and returns the updated analyst.
 *
 * @param {string} analystId - id of the analyst.
 * @param {ScenarioAnalysis[]} scenarios - scenarios to put (create or update by id).
 * @returns {Promise<AnalystProto | null>} The updated analyst, or null on failure.
 */
export const putScenarios = async (
	analystId: string,
	scenarios: ScenarioAnalysis[]
): Promise<AnalystProto | null> => {
	try {
		const body = JSON.stringify(scenarios.map((s) => ScenarioAnalysis.toJSON(s)));
		const response = await fetch(
			`${process.env.REACT_APP_PUT_SCENARIOS_TO_ANALYST!}?analystId=${encodeURIComponent(analystId)}`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body,
				credentials: "include",
			}
		);
		if (!response.ok) {
			showToast("Error putting scenarios", "", "error");
			return null;
		}
		const data = await response.json();
		return AnalystProto.fromJSON(data);
	} catch (error) {
		showToast("Error putting scenarios", "", "error");
		return null;
	}
};

/**
 * Refresh Analyst Data Scope
 *
 * @param {string} analystId - id of the analyst to refresh the data scope.
 * @param {boolean} asyncExecution - whether to execute the refresh in async mode.
 * @returns {Promise<void>} A promise that resolves to void.
 * @throws {Error} Throws an error if the request fails.
 */
export const refreshAnalystDataScope = async (analystId: string, asyncExecution: boolean = true) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_REFRESH_DATA_SCOPE!}?analystId=${analystId}&asyncExecution=${asyncExecution}`, {
			method: "POST",
			credentials: "include",
		});
		if (!response.ok) {
			showToast("Error refreshing analyst data scope", "", "error");
			return null;
		}

		return response;
	}
	catch (error) {
		showToast("Error refreshing analyst data scope", "", "error");
		return null;
	}
};

/**
 * Delete Analyst
 *
 * @param {string} analystId - id of the analyst to delete.
 * @returns {Promise<boolean>} A promise that resolves to true if the analyst is deleted.
 * @throws {Error} Throws an error if the request fails.
 */
export const deleteAnalyst = async (analystId: string) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_ANALYST!}/${analystId}`, {
			method: "DELETE",
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Error deleting analyst", "", "error");
			return null;
		}

		return response;
	} catch (error) {
		showToast("Error deleting analyst", "", "error");
		return null;
	}
};

/**
 * Get List of Analysts
 *
 * @param {number} pageSize - page size to fetch the analysts.
 * @param {number} pageNumber - page number to fetch the analysts.
 * @param {string} sortByField - field to sort the analysts.
 * @param {boolean} sortAscending - sort ascending or descending.
 * @param {string} searchString - search string to filter the analysts.
 * @param {string} author - author id to filter the analysts.
 * @returns {Promise<Header[]>} A promise that resolves to the list of analysts.
 * @throws {Error} Throws an error if the request fails.
 */
export const getListOfAnalysts = async (
	pageSize: number = 20,
	pageNumber: number = 1,
	sortByField?: string,
	sortAscending?: boolean,
	searchString?: string,
	author?: string,
) => {
	try {
		// Build URL with only defined parameters
		const params = new URLSearchParams();
		params.append("pageSize", pageSize.toString());
		params.append("pageNumber", pageNumber.toString());

		if (sortByField) {
			params.append("sortByField", sortByField);
		}
		if (sortAscending !== undefined) {
			params.append("sortAscending", sortAscending.toString());
		}
		if (searchString && searchString.trim()) {
			params.append("searchString", searchString.trim());
		}
		if (author && author.trim()) {
			params.append("author", author.trim());
		}

		const response = await fetch(`${process.env.REACT_APP_LIST_ANALYST!}?${params.toString()}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Error getting list of analysts", "", "error");
			return null;
		}

		const data = await response.json();
		return { headers: data.headers.map((header: Header[]) => Header.fromJSON(header)), totalRows: data.total_rows };
	} catch (error) {
		showToast("Error getting list of analysts", "", "error");
		return null;
	}
};

/**
 * Clone Analyst
 *
 * @param {string} analystId - id of the analyst to clone.
 * @param {string} name - name of the cloned analyst.
 * @param {boolean} cloneJobs - whether to clone the jobs.
 * @param {boolean} save - whether to save the cloned analyst.
 *
 * @returns {Promise<AnalystProto>} A promise that resolves to the cloned analyst.
 * @throws {Error} Throws an error if the request fails.
 */
export const cloneAnalyst = async (
	analystId: string,
	name?: string,
	cloneJobs?: boolean,
	save?: boolean,
) => {
	try {
		const params = new URLSearchParams();
		params.append("analystId", analystId);
		if (name) {
			params.append("name", name);
		}
		if (cloneJobs) {
			params.append("cloneJobs", cloneJobs.toString());
		}
		if (save) {
			params.append("save", save.toString());
		}
		const response = await fetch(`${process.env.REACT_APP_CLONE_ANALYST!}?${params.toString()}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Error cloning analyst", "", "error");
			return null;
		}

		const data = await response.json();
		showToast("Analyst cloned successfully", "", "success");
		return AnalystProto.fromJSON(data);
	} catch (error) {
		showToast("Error cloning analyst", "", "error");
		return null;
	}
};

/**
 * Get a list of Slack channels.
 *
 * @returns {Promise<SlackChannel[] | null>} A promise that resolves to the list of Slack channels.
 * @throws {Error} Throws an error if there is an issue getting the Slack channels or if the request fails.
*/
export const getSlackChannels = async (): Promise<SlackChannel[] | null> => {
	try {
		const response = await fetch(`${process.env.REACT_APP_SLACK_CHANNELS}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Failed to get Slack channels", "", "error");
			return null;
		}

		const data = await response.json();
		return data.map((channel: SlackChannel) => SlackChannel.fromJSON(channel));
	} catch (error) {
		showToast("Error in getting Slack channels", "", "error");
		return null;
	}
};

/**
 * Get a list of Slack channels users.
 *
 * @returns {Promise<SlackUser[] | null>} A promise that resolves to the list of Slack channels.
 * @throws {Error} Throws an error if there is an issue getting the Slack channels or if the request fails.
*/
export const getSlackChannelUsers = async (channelId: string | undefined): Promise<SlackUser[] | null> => {
	try {
		const response = await fetch(`${process.env.REACT_APP_SLACK_CHANNELS}/${channelId}/users`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Failed to get Slack Channel Users", "", "error");
			return null;
		}

		const data = await response.json();
		return data.map((user: SlackUser) => SlackUser.fromJSON(user));
	} catch (error) {
		showToast("Error in getting Slack Channel Users", "", "error");
		return null;
	}
};